import { useState } from 'react';

import { ChartFigure } from './ChartFigure';
import { fmt, stepPath, volumeGeometry } from './geometry';

export interface VolumePoint {
  /** X-axis label, e.g. `12 Jun`. */
  label: string;
  /** Long label for the tooltip, e.g. `Tue 14 Jul`. */
  longLabel: string;
  value: number;
}

/** The ways this chart can render the same underlying data/scale. */
export type VolumeChartType = 'area' | 'line' | 'step' | 'bar' | 'scatter';

export interface LineAreaProps {
  points: readonly VolumePoint[];
  /** Accessible series summary. */
  summary: string;
  /** Visual mode — the geometry, crosshair and hidden table are identical
   * either way; only what's drawn in the `<svg>` changes. Default `area`
   * matches this chart's original, only-ever behaviour. */
  chartType?: VolumeChartType;
}

/**
 * §5.2 Volume over time — line, area or bar, with a crosshair tooltip,
 * gridlines and y-labels. The tooltip is reachable by BOTH mouse (move) and
 * keyboard (Left/Right steps the active point, Home/End jump) — the
 * design-reference §8.6.14 fix for the mouse-only mock tooltip. A hidden data
 * table carries every day for screen readers.
 */
/**
 * Width reserved to the left of the plot for the y-axis labels, in CSS pixels.
 *
 * Pixels, not viewBox units: the labels are HTML at a fixed 10px font, so the
 * space they need does not scale with the chart. Wide enough for the longest label
 * the axis produces ("7.5k", "41k") plus the 8px it is inset from the plot.
 */
const AXIS_GUTTER_PX = 38;

/**
 * How to hang the crosshair label off a point at `x` (viewBox units).
 *
 * Centred over the point everywhere except the outer fifth of the plot, where
 * centring put half the label outside the card — at the first point it was clipped
 * mid-word. There it hangs inward from the point instead.
 */
function tooltipAnchor(x: number): string {
  if (x < 720 * 0.2) return 'translateX(-8px)';
  if (x > 720 * 0.8) return 'translateX(calc(-100% + 8px))';
  return 'translateX(-50%)';
}

export function LineArea({ points, summary, chartType = 'area' }: LineAreaProps): React.JSX.Element {
  const values = points.map((p) => p.value);
  const geo = volumeGeometry(values);
  const n = points.length;
  const [active, setActive] = useState<number | null>(null);
  // 60% of each point's slot width, so neighbouring bars leave a visible gap;
  // floored at 2px so a long series (many points) never draws a zero-width bar,
  // and capped at 44 so a *short* one does not draw a slab. A two-day series gave
  // each point 60% of half the plot — 216 units — which is not a bar chart, and
  // the outermost half of each of those was clipped by the viewBox edge.
  const barWidth = n > 0 ? Math.min(44, Math.max(2, (720 / n) * 0.6)) : 0;

  const onMove = (event: React.MouseEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    const i = Math.round(((event.clientX - rect.left) / rect.width) * (n - 1));
    if (i >= 0 && i < n) setActive(i);
  };

  const step = (delta: number): void => {
    setActive((current) => {
      if (current === null) return delta > 0 ? 0 : n - 1;
      return Math.max(0, Math.min(n - 1, current + delta));
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(n - 1);
    } else if (event.key === 'Escape') {
      setActive(null);
    }
  };

  const activePoint = active !== null ? geo.points[active] : undefined;
  const activeDatum = active !== null ? points[active] : undefined;

  return (
    <ChartFigure
      summary={summary}
      columns={['Day', 'Documents']}
      tableCaption="Documents ingested per day"
      rows={points.map((p) => ({ label: p.longLabel, values: { Documents: fmt(p.value) } }))}
    >
      {/* `paddingLeft` reserves the y-axis label gutter in PIXELS. The labels used
          to sit at `left: 0` over the plot itself, so the line ran straight
          through "7.5k" and "5.0k". A viewBox-relative margin cannot do this job:
          it scales with the element, so on a narrow card it would shrink below
          the labels' own width and they would be back on the plot. The
          interaction surface and the SVG both sit inside this content box, so
          pointer maths still lines up with what is drawn. */}
      <div style={{ position: 'relative', paddingLeft: AXIS_GUTTER_PX }}>
        {/* A keyboard-navigable chart region (WCAG §8.6.14): role="application"
            with arrow-key stepping and a mouse crosshair. The rules below assume
            a static div, but this element is deliberately interactive. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          style={{ position: 'relative' }}
          tabIndex={0}
          role="application"
          aria-label="Volume chart. Use Left and Right arrows to inspect each day."
          onMouseMove={onMove}
          onMouseLeave={() => { setActive(null); }}
          onKeyDown={onKeyDown}
          data-testid="linearea-surface"
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          <svg width="100%" height="200" viewBox="0 0 720 200" preserveAspectRatio="none" fill="none" style={{ display: 'block' }} role="img" aria-label={summary}>
            {geo.gridY.map((y) => (
              <line key={y} x1="0" y1={y} x2="720" y2={y} stroke="var(--grid)" />
            ))}
            {chartType === 'bar' ? (
              geo.points.map(([x, y], i) => (
                <rect
                  key={points[i]?.label ?? i}
                  x={x - barWidth / 2}
                  y={y}
                  width={barWidth}
                  height={Math.max(0, 168 - y)}
                  rx="1.5"
                  fill={i === active ? 'var(--a-ink)' : 'var(--accent)'}
                />
              ))
            ) : chartType === 'scatter' ? (
              geo.points.map(([x, y], i) => (
                <circle
                  key={points[i]?.label ?? i}
                  cx={x}
                  cy={y}
                  r={i === active ? 4.5 : 3}
                  fill={i === active ? 'var(--a-ink)' : 'var(--accent)'}
                />
              ))
            ) : (
              <>
                {chartType === 'area' ? <path d={geo.area} fill="var(--a-soft)" /> : null}
                <path
                  d={chartType === 'step' ? stepPath(geo.points) : geo.line}
                  stroke="var(--accent)"
                  strokeWidth="2"
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
            {activePoint !== undefined && (chartType === 'area' || chartType === 'line' || chartType === 'step') ? (
              <>
                <line x1={activePoint[0]} y1="22" x2={activePoint[0]} y2="168" stroke="var(--t3)" strokeDasharray="3 3" />
                <circle cx={activePoint[0]} cy={activePoint[1]} r="4.5" fill="var(--card)" stroke="var(--accent)" strokeWidth="2" />
              </>
            ) : null}
          </svg>
          {activeDatum !== undefined && active !== null && activePoint !== undefined ? (
            <div
              role="status"
              style={{
                position: 'absolute',
                top: 2,
                // The point's own x, not `active / (n - 1)`: the series is inset
                // from the plot edges, so the even split no longer lands on the
                // marker it is describing.
                left: `${((activePoint[0] / 720) * 100).toFixed(1)}%`,
                // Centred, except near the ends, where centring pushed half the
                // label off the card — at the first point it was clipped mid-word.
                transform: tooltipAnchor(activePoint[0]),
                background: 'var(--t1)',
                color: 'var(--bg)',
                borderRadius: 8,
                padding: '7px 11px',
                fontSize: 11,
                lineHeight: 1.5,
                boxShadow: 'var(--sh2)',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <strong>{activeDatum.longLabel}</strong> — {fmt(activeDatum.value)} documents
            </div>
          ) : null}
        </div>
        {/* Padding pins the first/last label to the first/last gridline exactly —
            30px (`geo.gridY[0]`) top, 66.5px (200 svg-height − `geo.gridY[3]`)
            bottom — so the printed numbers land next to the lines they describe
            instead of drifting from a second, independently-tuned padding guess. */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            // Absolute offsets resolve against the padding box, so `left: 0` is
            // the outer edge — i.e. the gutter reserved above.
            width: AXIS_GUTTER_PX,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            padding: '30px 8px 66.5px 0',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        >
          {geo.yLabels.map((labelText, i) => (
            <span key={i} style={{ fontSize: 10, color: 'var(--t3)' }}>
              {labelText}
            </span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--t3)', paddingLeft: AXIS_GUTTER_PX }}>
        {points
          .filter((_, i) => i % Math.max(1, Math.floor(n / 7)) === 0)
          .map((p) => (
            <span key={p.label}>{p.label}</span>
          ))}
      </div>
    </ChartFigure>
  );
}
